import styled from '@emotion/styled';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { rem } from 'polished';
import React from 'react';
import $ from 'text2vdom';

import ContentBox from '~/components/ContentBox';
import MobileLineBreak from '~/components/MobileLineBreak';
import type { BackgroundImageQuery } from '~/generated/types/gatsby';
import { mq } from '~/shared/utils/mq';

import SectionBase, { SectionBaseProps } from './base/ovensmash';

export interface OvensmashBackgroundImageSectionProps extends SectionBaseProps {
  subtitle?: string;
  description: string;
  image: {
    pc: string;
    mobile: string;
  };
}

const Wrapper = styled.section(
  mq({
    background: '#090041',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: rem(30),
    paddingBottom: rem(60),
    paddingInline: rem(10),
    color: '#fff',
    '> .pc': {
      display: ['none', 'block'],
    },
    '> .mobile': {
      display: ['block', 'none'],
    },
  }),
);

const SubTitle = styled.h3(
  mq({
    margin: 0,
    marginBottom: rem(16),
    width: '100%',

    fontSize: [rem(24), rem(32)],
    fontWeight: 700,
    textAlign: 'center',
    lineHeight: 'normal',
    whiteSpace: 'pre-line',
    ':lang(ja)': {
      fontWeight: 700,
    },
    ':lang(zh-Hant)': {
      fontWeight: 700,
    },
    ':lang(th)': {
      fontWeight: 700,
    },
  }),
);

const Description = styled('p')(
  mq({
    marginTop: rem(16),
    fontSize: rem(14),
    lineHeight: 'normal',
    textAlign: 'center',
    whiteSpace: 'pre-line',
    margin: 0,
    '> br': {
      display: ['block', 'none'],
    },
  }),
);

const OvensmashBackgroundImageSection: React.FC<OvensmashBackgroundImageSectionProps> = ({
  id,
  title,
  subtitle,
  description,
  image,
}) => {
  const data = useStaticQuery<BackgroundImageQuery>(graphql`
    query OvensmashBackgroundImage {
      allPcBackgroundImage: allFile(filter: { relativePath: { regex: "/games/ovensmash/background/" } }) {
        nodes {
          relativePath
          childImageSharp {
            gatsbyImageData(width: 800, breakpoints: [640, 1200], formats: [AUTO, WEBP])
          }
        }
      }
      allMobileBackgroundImage: allFile(filter: { relativePath: { regex: "/games/ovensmash/background/" } }) {
        nodes {
          relativePath
          childImageSharp {
            gatsbyImageData(width: 340, breakpoints: [340, 640], formats: [AUTO, WEBP])
          }
        }
      }
    }
  `);

  const pcBgNode = data.allPcBackgroundImage.nodes.find((node) => node.relativePath === image.pc);
  const mobileBgNode = data.allMobileBackgroundImage.nodes.find((node) => node.relativePath === image.mobile);

  const mbr = () => <MobileLineBreak />;

  return (
    <Wrapper id={id}>
      <SectionBase Tag='div' title={title}>
        {subtitle && <SubTitle className='font-cookierun-bold'>{$(subtitle, { mbr })}</SubTitle>}
        <ContentBox>
          <Description>{$(description, { mbr })}</Description>
        </ContentBox>
      </SectionBase>
      {pcBgNode && <GatsbyImage className='pc' image={pcBgNode.childImageSharp.gatsbyImageData} alt={title || ''} />}
      {mobileBgNode && (
        <GatsbyImage className='mobile' image={mobileBgNode.childImageSharp.gatsbyImageData} alt={title || ''} />
      )}
    </Wrapper>
  );
};

export default OvensmashBackgroundImageSection;
